import React, { useEffect, useState } from 'react';
import 'react-dates/initialize';
import { SingleDatePicker, DayPickerSingleDateController } from 'react-dates';
import moment, { Moment } from 'moment';
import 'react-dates/lib/css/_datepicker.css';
import classNames from 'classnames';
import Icon from 'react/components/icon';

interface DateInputProps {
  value?: string;
  onChange: (value: any) => void;
  disabled?: boolean;
  placeholder?: string;
  mode?: 'datePicker' | 'dayPicker';
  minDate?: string;
  maxDate?: string;
  hideClearDate?: boolean;
  appendToBody?: boolean;
  fullWidth?: boolean;
  variant?: '36' | 'v2';
}

const DateInput = ({
  value,
  onChange,
  disabled,
  mode,
  minDate,
  maxDate,
  hideClearDate,
  appendToBody,
  variant,
  fullWidth,
  placeholder,
}: DateInputProps) => {
  const [id] = useState(Math.floor(Math.random() * 100000).toString());
  const [focused, setFocused] = useState(false);
  const [keepFocused, setKeepFocused] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) {
      window.addEventListener('mousedown', onMouseDown);
    }

    return () => {
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [focused]);

  const onMouseDown = (event) => {
    const isScrollbarClick = window.innerWidth - event.clientX < 16;

    setKeepFocused(!!isScrollbarClick);

    if (variant === 'v2' && containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setFocused(false);
    }
  };

  const _onChange = (val) => {
    if (disabled) return;

    if (!val) return onChange(val);

    onChange(moment(val).format('YYYY-MM-DD'));
  };

  const getValue = () => {
    if (!value) return null;

    return moment(value);
  };

  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => {
    let i;
    const years = [];
    for (i = moment().year() + 10; i >= moment().year() - 100; i--) {
      years.push(
        <option value={i} key={`year-${i}`}>
          {i}
        </option>,
      );
    }
    return (
      <div className="datepicker__month-select">
        <div>
          <select value={month.month()} onChange={(e) => onMonthSelect(month, e.target.value)}>
            {moment.months().map((label, value) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select value={month.year()} onChange={(e) => onYearSelect(month, e.target.value)}>
            {years}
          </select>
        </div>
      </div>
    );
  };

  const isDayBlocked = (day: Moment) => {
    if (minDate && day.isBefore(minDate, 'day')) return true;
    if (maxDate && day.isAfter(maxDate, 'day')) return true;
  };

  const onFocusChange = (focused) => {
    if (keepFocused) return;

    setFocused(focused);
  };

  if (mode === 'dayPicker') {
    return (
      <div className="datepicker">
        <DayPickerSingleDateController
          onDateChange={_onChange}
          isDayBlocked={isDayBlocked}
          date={getValue()}
          numberOfMonths={1}
          hideKeyboardShortcutsPanel
          transitionDuration={0}
          noBorder
          focused={focused}
          onFocusChange={({ focused }) => onFocusChange(focused)}
          initialVisibleMonth={() => getValue() || moment()}
        />
      </div>
    );
  }

  if (variant === 'v2') {
    return (
      <div ref={containerRef} style={{ position: 'relative' }}>
        <div
          className={classNames('date-input input-group', {
            'date-input--v2': true,
            'date-input--full-width': fullWidth,
            'date-input--disabled': disabled,
          })}
          onClick={() => !disabled && setFocused(true)}
        >
          <input
            className="form-control form-control--v2"
            type="text"
            placeholder={placeholder}
            value={getValue() ? getValue()!.format('DD MMM YYYY') : ''}
            readOnly
            disabled={disabled}
            style={{ color: disabled ? undefined : '#000' }}
          />
          <div className="date-input__backdrop" />
          <span className="input-group-btn">
            <button
              className="btn btn-default date-input__button"
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                setFocused(true);
              }}
            >
              <Icon name="calendar" width={20} height={20} />
            </button>
          </span>
        </div>
        {focused && (
          <div style={{ position: 'absolute', zIndex: 1000, marginTop: '4px', left: 0, right: 0 }}>
            <DayPickerSingleDateController
              onDateChange={(date) => {
                _onChange(date);
                setFocused(false);
              }}
              isDayBlocked={isDayBlocked}
              date={getValue()}
              numberOfMonths={1}
              hideKeyboardShortcutsPanel
              transitionDuration={0}
              noBorder
              focused={focused}
              onFocusChange={({ focused }) => onFocusChange(focused)}
              initialVisibleMonth={() => getValue() || moment()}
              renderMonthElement={renderMonthElement}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames('datepicker', {
        'datepicker--36': variant === '36',
        'datepicker--full-width': fullWidth,
      })}
    >
      <SingleDatePicker
        id={id}
        onDateChange={_onChange}
        onFocusChange={({ focused }) => onFocusChange(focused)}
        date={getValue()}
        focused={focused}
        numberOfMonths={1}
        hideKeyboardShortcutsPanel
        showDefaultInputIcon
        showClearDate={!hideClearDate}
        isDayBlocked={isDayBlocked}
        placeholder={placeholder || ''}
        displayFormat="DD MMM YYYY"
        renderMonthElement={renderMonthElement}
        transitionDuration={0}
        isOutsideRange={() => false}
        appendToBody={appendToBody}
      />
    </div>
  );
};

export default DateInput;
